// Genera y descarga una Guía de Envío en formato media carta (5.5 × 8.5 pulgadas)
// lista para imprimir y pegar en el empaque de Interrapidísimo.
//
// Usa jsPDF para generar un PDF 100% en el lado del cliente, sin servidor.
// IMPORTANTE: jsPDF se importa de forma ESTÁTICA y todo el flujo es SÍNCRONO para
// conservar el "gesto del usuario". Cualquier `await` antes de la descarga hace que
// el navegador abra una pestaña en blanco o ignore el atributo `download`.
import { jsPDF } from 'jspdf';
import type { BrandLogo } from '@/lib/brandAssets';

export interface ShippingLabelData {
  // --- Remitente (empresa) ---
  companyName: string;
  companyPhone: string;
  companyCity: string;
  companyLogo?: BrandLogo | null;
  // --- Datos del remitente para imprimir junto a los del cliente ---
  senderName: string;
  senderId: string;
  senderPhone: string;
  senderAddress: string;
  senderCity: string;
  // --- Destinatario ---
  recipientName: string;
  recipientPhone: string;
  recipientId: string;
  recipientEmail: string;
  recipientCity: string;
  recipientDepartment: string;
  recipientAddress: string;
  recipientNotes: string;
  // --- Pedido ---
  orderNumber: string;
  orderDate: string;
  contents: string;
  paymentMode: string; // "Cobro Contraentrega" | "Flete Pagado"
  total: number;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export function downloadShippingLabel(data: ShippingLabelData): void {
  // Media carta: 5.5 × 8.5 pulgadas → en mm: ~139.7 × 215.9.
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [139.7, 215.9] });

  const PW = 139.7;
  const margin = 8;
  const contentW = PW - margin * 2;
  let y = 10;

  function line(
    text: string,
    size: number,
    bold = false,
    align: 'left' | 'center' | 'right' = 'left',
    x = margin,
    maxW = contentW,
  ) {
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(text, align === 'center' ? PW / 2 : align === 'right' ? PW - margin : x, y, {
      align,
      maxWidth: maxW,
    });
  }

  function lines(text: string, size: number, bold = false): number {
    doc.setFontSize(size);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const wrapped = doc.splitTextToSize(text, contentW);
    doc.text(wrapped, margin, y);
    return (wrapped.length - 1) * (size * 0.352778) * 1.4;
  }

  function rule(thick = 0.3) {
    doc.setLineWidth(thick);
    doc.line(margin, y, PW - margin, y);
    y += 2;
  }

  function sectionHeader(label: string) {
    doc.setFillColor(30, 30, 30);
    doc.rect(margin, y - 1, contentW, 6, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(label.toUpperCase(), margin + 2, y + 3.5);
    doc.setTextColor(0, 0, 0);
    y += 8;
  }

  function field(label: string, value: string) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(label.toUpperCase() + ':', margin, y);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    const wrapped = doc.splitTextToSize(value || '—', contentW);
    doc.text(wrapped, margin, y + 4);
    y += 4 + (wrapped.length - 1) * 10 * 0.352778 * 1.4 + 6;
  }

  // Igual que field() pero omite el campo si no tiene valor (para no imprimir
  // guiones vacíos en los datos opcionales del remitente).
  function fieldMaybe(label: string, value: string) {
    if (!value) return;
    field(label, value);
  }

  // ─── ENCABEZADO ───
  // Logo real de la marca (si está configurado) + datos de la empresa.
  const hasLogo = !!data.companyLogo;
  const headerH = hasLogo ? 36 : 26;
  doc.setFillColor(245, 245, 245);
  doc.rect(0, 0, PW, headerH, 'F');

  let headY = 5;
  if (hasLogo && data.companyLogo) {
    const { dataUrl, width, height } = data.companyLogo;
    const ratio = height > 0 ? width / height : 1;
    let drawH = 13;
    let drawW = drawH * ratio;
    if (drawW > 70) {
      drawW = 70;
      drawH = ratio > 0 ? drawW / ratio : 13;
    }
    try {
      doc.addImage(dataUrl, 'PNG', (PW - drawW) / 2, headY, drawW, drawH);
      headY += drawH + 1.5;
    } catch {
      headY += 1;
    }
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(data.companyName || 'PCP \u2014 Paticas con Prop\u00f3sito', PW / 2, headY + 4, { align: 'center' });
  headY += 9;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text(`${data.companyPhone || ''}   ${data.companyCity || 'Bogot\u00e1 D.C.'}`, PW / 2, headY, { align: 'center' });

  y = headerH - 2;
  rule(0.5);
  y += 2;

  // Número de pedido y fecha – visible desde lejos
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(80, 80, 80);
  doc.text(`Pedido: ${data.orderNumber}`, margin, y);
  doc.text(`Fecha: ${formatDate(data.orderDate)}`, PW - margin, y, { align: 'right' });
  y += 7;

  rule(0.2);
  y += 2;

  // ─── REMITENTE (datos que salen junto a los del cliente) ───
  const hasSender = !!(
    data.senderName || data.senderId || data.senderPhone || data.senderCity || data.senderAddress
  );
  if (hasSender) {
    sectionHeader('Remitente');
    fieldMaybe('Nombre', data.senderName || data.companyName);
    fieldMaybe('C\u00e9dula / NIT', data.senderId);
    fieldMaybe('Tel\u00e9fono', data.senderPhone || data.companyPhone);
    fieldMaybe('Ciudad', data.senderCity || data.companyCity);
    fieldMaybe('Direcci\u00f3n', data.senderAddress);

    rule(0.2);
    y += 2;
  }

  // ─── DESTINATARIO ───
  sectionHeader('Destinatario');

  field('Nombre', data.recipientName);
  field('Tel\u00e9fono / Celular', data.recipientPhone);
  field('C\u00e9dula / NIT', data.recipientId);
  fieldMaybe('Correo electr\u00f3nico', data.recipientEmail);

  const cityDept = [data.recipientCity, data.recipientDepartment].filter(Boolean).join(' \u2014 ');
  field('Ciudad / Departamento', cityDept);
  field('Direcci\u00f3n Exacta', data.recipientAddress);

  if (data.recipientNotes) {
    field('Notas de entrega / Indicaciones', data.recipientNotes);
  }

  rule(0.2);
  y += 2;

  // ─── CONTENIDO ───
  sectionHeader('Contenido del paquete');

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  const extraLines = lines(data.contents || 'Babuchas / Paticas con Prop\u00f3sito', 9);
  y += 5 + extraLines;

  // ─── MODALIDAD Y TOTAL ───
  rule(0.3);
  y += 2;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  line('Modalidad:', 9, true, 'left');
  y += 5;
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  line(data.paymentMode || 'Cobro Contraentrega', 11, true, 'center');
  y += 8;

  if (data.total > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    line(`Valor a cobrar: $${data.total.toLocaleString()}`, 9, true, 'center');
    y += 7;
  }

  // ─── PIE ───
  rule(0.5);
  y += 3;
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  line('Incluye: Bandana de la Manada + Bolsa Ecol\u00f3gica PCP', 7, false, 'center');
  y += 5;
  line('www.paticasconproposito.com', 7, false, 'center');

  // Descarga con el método NATIVO de jsPDF (.save()): baja el PDF con su tipo MIME
  // correcto (application/pdf) para que sea un archivo legible y ejecutable, no un
  // binario sin formato. Nombre unificado con prefijo de marca y transportadora:
  // p. ej. Guia_Interrapidisimo_MediaCarta_ORDER102.pdf
  const safeNum = (data.orderNumber || 'guia').replace(/[^a-zA-Z0-9-]/g, '_');
  const fileName = `Guia_Interrapidisimo_MediaCarta_${safeNum}.pdf`;

  doc.save(fileName);
}